/**
 * HOOK: useSelectData
 * Hook générique léger pour charger uniquement les données nécessaires aux selects
 *
 * ⚡ Optimisation: Ne charge que les colonnes nécessaires (id, name par défaut)
 * 🎯 Usage: Remplace les hooks complets (useProducts, useVehicles, etc.) dans les formulaires
 *
 * @example
 * const { data: products, loading } = useSelectData('products');
 * const { data: vehicles, loading } = useSelectData('vehicles', 'id, registration');
 */

import { useEffect, useState } from 'react';
import { useSupabase } from './useSupabase';

interface SelectDataItem {
  id: string;
  name?: string;
  [key: string]: any;
}

interface UseSelectDataReturn {
  data: SelectDataItem[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Hook générique pour charger des données légères destinées aux selects
 *
 * @param tableName - Nom de la table Supabase
 * @param columns - Colonnes à sélectionner (défaut: 'id, name')
 * @param orderColumn - Colonne de tri (défaut: 'name')
 * @param ascending - Ordre de tri (défaut: true)
 */
export function useSelectData(
  tableName: string,
  columns: string = 'id, name',
  orderColumn: string = 'name',
  ascending: boolean = true
): UseSelectDataReturn {
  const supabase = useSupabase();
  const [data, setData] = useState<SelectDataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  async function fetchData() {
    try {
      setLoading(true);
      setError(null);

      const { data: result, error: fetchError } = await supabase
        .from(tableName)
        .select(columns)
        .order(orderColumn, { ascending });

      if (fetchError) throw fetchError;

      // Double cast pour résoudre les incompatibilités de types Supabase
      setData((result as unknown as SelectDataItem[]) || []);
    } catch (err) {
      console.error(`[useSelectData:${tableName}] Error:`, err);
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName, columns, orderColumn, ascending]);

  return {
    data,
    loading,
    error,
    refresh: fetchData,
  };
}
